<script>
	// imports
	import registrationTypes from "../../config/registrationTypes";
	import { projectSettings } from "../../config";
	import model from "../../stores/modelStore";
	import { isValid } from "../../validation";
	import { registerPageComplete } from "../../tealium";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import DecimalInput from "../../components/DecimalInput.svelte";
	import Select from "../../components/Select.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import CheckboxButton from "../../components/CheckboxButton.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import GeneticDiagnosisList from "../../components/GeneticDiagnosisList.svelte";
	import ConditionDiagnosisList from "../../components/ConditionDiagnosisList.svelte";
	import SaveAndClose from "../../components/SaveAndClose.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import ClassActionPhoneNumber from "../../components/ClassActionPhoneNumber.svelte";
	import ClassActionEmailAddress from "../../components/ClassActionEmailAddress.svelte";

	// props
	export let pageName = null;
	export let persistUrl = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	let validated = false,
		geneticValid = false,
		conditionValid = false,
		showGeneticOther = false,
		showConditionOther = false,
		missingGeneticError = false,
		missingConditionError = false;

	let buttons = null;

	const no = "No",
		beenThrough = "Yes, I have been through menopause",
		goingThrough = "Yes, I am going through menopause",
		menopauseOptions = [{ name: no }, { name: beenThrough }, { name: goingThrough }];

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = [...isValid(), ...customValidator()];

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
				//displayError(response);
			} else {
				registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);
				buttons.next();
			}
		} else {
			try {
				errors[0].scrollIntoView({ behavior: "smooth" });
			} catch (e) {
				console.log("PersonalMedicalHistory Exception", e);
			}
		}

		buttons.unblock();
	};

	const customValidator = () => {
		validated = true;
		let errors = [];

		if (!geneticValid) {
			errors = [missingGeneticError];
		}

		if (!conditionValid) {
			errors = [...errors, missingConditionError];
		}

		return errors;
	};

	const handleNoneOfAboveChange = async (e, type) => {
		if (type == "CONDITION") {
			conditionValid = e.target.checked;

			if (e.target.checked) {
				$model.personalMedicalHistory.conditionDiagnoses.forEach((c) => {
					c.checked = false;
				});
				showConditionOther = false;
				$model.personalMedicalHistory.otherConditionType = null;
			}
		} else {
			geneticValid = e.target.checked;

			if (e.target.checked) {
				$model.personalMedicalHistory.geneticConditions.forEach((c) => {
					c.checked = false;
				});
				showGeneticOther = false;
				$model.personalMedicalHistory.otherGeneticConditionType = null;
			}
		}
	};

	const handleDiagnosisChange = async (e, type) => {
		if (e.detail.target.checked) {
			if (type == "CONDITION") {
				conditionValid = true;
				$model.personalMedicalHistory.noConditionDiagnosis = false;
			} else {
				geneticValid = true;
				$model.personalMedicalHistory.noGeneticConditionTest = false;
			}
		} else {
			if (type == "CONDITION") {
				conditionValid = $model.personalMedicalHistory.conditionDiagnoses.some((_) => _.checked);
			} else {
				geneticValid = $model.personalMedicalHistory.geneticConditions.some((_) => _.checked);
			}
		}

		if (type == "CONDITION") {
			if (e.detail.option.conditionName === "Other chronic illnesses or disabilities") {
				showConditionOther = e.detail.target.checked;
				if (!e.detail.target.checked) {
					$model.personalMedicalHistory.otherConditionType = null;
				}
			}
		} else {
			if (e.detail.option.geneticConditionName === "Other") {
				showGeneticOther = e.detail.target.checked;
				if (!e.detail.target.checked) {
					$model.personalMedicalHistory.otherGeneticConditionType = null;
				}
			}
		}
	};

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const initialise = () => {
		geneticValid =
			$model.personalMedicalHistory.geneticConditions.some((_) => _.checked) ||
			$model.personalMedicalHistory.noGeneticConditionTest;
		conditionValid =
			$model.personalMedicalHistory.conditionDiagnoses.some((_) => _.checked) ||
			$model.personalMedicalHistory.noConditionDiagnosis;

		showGeneticOther = $model.personalMedicalHistory.geneticConditions.some(
			(_) => _.checked && _.geneticConditionName == "Other"
		);
		showConditionOther = $model.personalMedicalHistory.conditionDiagnoses.some(
			(_) => _.checked && _.conditionName === "Other chronic illnesses or disabilities"
		);
	};

	initialise();
</script>

<section>
	<Form {disabled} id="form-medical-history" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Personal Medical History</h1>
				<p>
					If you are unable to complete any part of this section of the form, we are here to assist you.
					Please contact us on <ClassActionPhoneNumber /> or via email to <ClassActionEmailAddress />.
				</p>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn cols="6">
					<DatePicker
						required
						{disabled}
						label="Date of birth"
						validator={true}
						start={new Date(1900, 0, 1)}
						end={new Date()}
						bind:formattedSelected={$model.personalMedicalHistory.dateOfBirth}
						openOnClass="calendar-icon"
					/>
				</FormColumn>

				{#if $model.registrationType && $model.registrationType == registrationTypes.types.DeceasedEstate.value}
					<FormColumn cols="6">
						<DatePicker
							required
							{disabled}
							label="Date of death"
							validator={true}
							start={new Date(1950, 0, 1)}
							end={new Date()}
							bind:formattedSelected={$model.personalMedicalHistory.dateOfDeath}
							openOnClass="calendar-icon"
						/>
					</FormColumn>
				{/if}
			</FormRow>

			<FormRow>
				<FormColumn cols="6">
					<NumberInput
						label="Height (cm)"
						placeholder="e.g. 165"
						required
						{disabled}
						minvalue={100}
						maxvalue={250}
						errorMessage="Provide a valid height"
						bind:value={$model.personalMedicalHistory.height}
					/>
				</FormColumn>
			</FormRow>

			<FormRow>
				<FormColumn>
					<!-- svelte-ignore a11y-label-has-associated-control -->
					<label>
						Weight during the five years prior to the cancer diagnosis
						<span class="required">*</span>
					</label>
				</FormColumn>
			</FormRow>

			<FormRow class="mt-0">
				<FormColumn cols="6">
					<NumberInput
						placeholder="Minimum weight [kgs]"
						required
						{disabled}
						minvalue={20}
						maxvalue={650}
						errorMessage="Provide a valid minimum weight"
						bind:value={$model.personalMedicalHistory.lowestWeight}
					/>
				</FormColumn>
				<FormColumn cols="6">
					<NumberInput
						placeholder="Maximum weight [kgs]"
						required
						{disabled}
						minvalue={20}
						maxvalue={650}
						errorMessage="Provide a valid maximum weight"
						bind:value={$model.personalMedicalHistory.heighestWeight}
					/>
				</FormColumn>
			</FormRow>

			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						required
						name="hadTubalLigation"
						label="Have you ever had a tubal ligation?"
						bind:value={$model.personalMedicalHistory.hadTubalLigation}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory.hadTubalLigation === "Yes"}
				<FormRow>
					<FormColumn cols="6">
						<DatePicker
							required
							{disabled}
							label="Date of Procedure"
							validator={true}
							start={new Date(1950, 0, 1)}
							end={new Date()}
							bind:formattedSelected={$model.personalMedicalHistory.dateTubalLigation}
							openOnClass="calendar-icon"
						/>
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						required
						name="hadHysterectomy"
						label="Have you ever had a hysterectomy?"
						bind:value={$model.personalMedicalHistory.hadHysterectomy}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory.hadHysterectomy === "Yes"}
				<FormRow>
					<FormColumn cols="6">
						<DatePicker
							required
							{disabled}
							label="Date of Procedure"
							validator={true}
							start={new Date(1950, 0, 1)}
							end={new Date()}
							bind:formattedSelected={$model.personalMedicalHistory.dateHysterectomy}
							openOnClass="calendar-icon"
						/>
					</FormColumn>
				</FormRow>
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						required
						name="smoked"
						label="Have you ever smoked cigarettes?"
						bind:value={$model.personalMedicalHistory.smoked}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory.smoked === "Yes"}
				<FormRow>
					<FormColumn>
						<YesNoGroup
							{disabled}
							required
							name="currentlySmoke"
							label="Do you currently smoke cigarettes?"
							bind:value={$model.personalMedicalHistory.currentlySmoke}
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn cols="6">
						<DecimalInput
							label="How long {$model.personalMedicalHistory.currentlySmoke !== 'No'
								? 'have'
								: 'did'} you {$model.personalMedicalHistory.currentlySmoke !== 'No'
								? 'smoked'
								: 'smoke'}? (approximate years)"
							placeholder="e.g. 1.5"
							required
							{disabled}
							minvalue={0}
							maxvalue={80}
							errorMessage="Provide a valid number of years"
							bind:value={$model.personalMedicalHistory.yearsSmoked}
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn cols="6">
						<!-- svelte-ignore a11y-label-has-associated-control -->
						<label>
							How many cigarettes/packs per day?
							<span class="required">*</span>
						</label>
					</FormColumn>
				</FormRow>

				<FormRow class="mt-0">
					<FormColumn cols="3">
						<NumberInput
							placeholder="e.g. 1.5"
							required
							{disabled}
							minvalue={0}
							maxvalue={80}
							errorMessage="Provide a valid number of years"
							bind:value={$model.personalMedicalHistory.numberPerDay}
						/>
					</FormColumn>
					<FormColumn cols="3">
						<Select
							required
							{disabled}
							id="numberPerDayType"
							bind:value={$model.personalMedicalHistory.numberPerDayType}
							optionList={[{ name: "Cigarettes" }, { name: "Packs" }]}
							textProperty="name"
							valueProperty="name"
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn>
						<TextArea
							label="{$model.personalMedicalHistory.currentlySmoke !== 'No'
								? 'Have'
								: 'Did'} your smoking habits {$model.personalMedicalHistory.currentlySmoke !== 'No'
								? 'changed'
								: 'change'} over time?"
							required
							{disabled}
							bind:value={$model.personalMedicalHistory.smokingHabitChange}
							id="smokingHabitChange"
						/>
					</FormColumn>
				</FormRow>

				{#if $model.personalMedicalHistory.currentlySmoke === "No"}
					<FormRow>
						<FormColumn cols="6">
							<DatePicker
								required
								{disabled}
								label="When did you stop smoking?"
								validator={true}
								start={new Date(1950, 0, 1)}
								end={new Date()}
								bind:formattedSelected={$model.personalMedicalHistory.dateStoppedSmoking}
								openOnClass="calendar-icon"
							/>
						</FormColumn>
					</FormRow>
				{/if}
			{/if}

			<FormRow>
				<FormColumn>
					<RadioGroup
						{disabled}
						required
						name="menopause"
						cols="12"
						label="Have you been, or are you going through, menopause?"
						optionList={menopauseOptions}
						labelProperty="name"
						valueProperty="name"
						bind:value={$model.personalMedicalHistory.menopause}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory.menopause === goingThrough || $model.personalMedicalHistory.menopause === beenThrough}
				<FormRow>
					<FormColumn>
						<YesNoGroup
							{disabled}
							required
							name="menopausePrior"
							label="Did you experience menopause before the diagnosis of cancer?"
							bind:value={$model.personalMedicalHistory.menopausePrior}
						/>
					</FormColumn>
				</FormRow>

				<FormRow>
					<FormColumn>
						<YesNoGroup
							{disabled}
							required
							name="menopauseSymptoms"
							label="{$model.personalMedicalHistory.menopause === goingThrough
								? 'Are'
								: 'Did'} you {$model.personalMedicalHistory.menopause === goingThrough
								? 'experiencing'
								: 'experience'} any menopausal symptoms?"
							bind:value={$model.personalMedicalHistory.menopauseSymptoms}
						/>
					</FormColumn>
				</FormRow>

				{#if $model.personalMedicalHistory.menopauseSymptoms === "Yes"}
					<FormRow>
						<FormColumn>
							<TextArea
								label="Please provide details"
								required
								{disabled}
								bind:value={$model.personalMedicalHistory.menopauseSymptomsDetails}
								id="menopauseSymptomsDetails"
							/>
						</FormColumn>
					</FormRow>
				{/if}
			{/if}

			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						required
						name="pregnant"
						label="Have you ever been pregnant?"
						bind:value={$model.personalMedicalHistory.pregnant}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory.pregnant === "Yes"}
				<FormRow>
					<FormColumn>
						<TextArea
							label="Please indicate how many times you have been pregnant, and provide dates for your pregnancies"
							required
							{disabled}
							bind:value={$model.personalMedicalHistory.pregnantPrior}
							id="pregnantPrior"
						/>
					</FormColumn>
				</FormRow>
			{/if}

			<p>
				<strong>
					Have you ever been tested for any of the below genetic mutations or conditions? Please select any
					relevant and provide the initial testing date, outcome of test, and diagnosis date (where
					appropriate).
				</strong>
			</p>

			<div class="shadowed-box mt-3" class:invalid={validated && !geneticValid}>
				<GeneticDiagnosisList
					{disabled}
					on:diagnosis-change={(e) => handleDiagnosisChange(e, "GENETIC")}
					bind:geneticDiagnoses={$model.personalMedicalHistory.geneticConditions}
				/>

				{#if showGeneticOther}
					<FormRow class="mt-2">
						<FormColumn>
							<TextInput
								label="Please specify other genetic tests"
								required
								{disabled}
								bind:value={$model.personalMedicalHistory.otherGeneticConditionType}
								id="otherGDiagnosis"
							/>
						</FormColumn>
					</FormRow>
				{/if}

				{#if !disabled || $model.personalMedicalHistory.noGeneticConditionTest}
					<FormRow class="mt-2">
						<FormColumn cols="3">
							<CheckboxButton
								{disabled}
								name="none-genetic"
								id="none-genetic"
								on:change={(e) => handleNoneOfAboveChange(e, "GENETIC")}
								bind:checked={$model.personalMedicalHistory.noGeneticConditionTest}
								label="None of the above"
							></CheckboxButton>
						</FormColumn>
					</FormRow>
				{/if}

				{#if validated && !geneticValid}
					<div bind:this={missingGeneticError} class="error-message">
						Please select a Genetic Test, or <b>None of the above</b>
					</div>
				{/if}
			</div>

			<p>
				<strong>
					Have you ever been diagnosed with any of the following? Select any relevant and provide the
					approximate date of initial diagnosis for each selection.
				</strong>
			</p>

			<div class="shadowed-box mt-3" class:invalid={validated && !conditionValid}>
				<ConditionDiagnosisList
					{disabled}
					on:diagnosis-change={(e) => handleDiagnosisChange(e, "CONDITION")}
					bind:conditionDiagnosis={$model.personalMedicalHistory.conditionDiagnoses}
				/>

				{#if showConditionOther}
					<FormRow class="mt-2">
						<FormColumn>
							<TextInput
								label="Please specify other chronic illnesses or disabilities"
								required
								{disabled}
								bind:value={$model.personalMedicalHistory.otherConditionType}
								id="otherGDiagnosis"
							/>
						</FormColumn>
					</FormRow>
				{/if}

				{#if !disabled || $model.personalMedicalHistory.noConditionDiagnosis}
					<FormRow class="mt-2">
						<FormColumn cols="6">
							<CheckboxButton
								{disabled}
								name="none-condition"
								id="none-condition"
								on:change={(e) => handleNoneOfAboveChange(e, "CONDITION")}
								bind:checked={$model.personalMedicalHistory.noConditionDiagnosis}
								label="None of the above"
							></CheckboxButton>
						</FormColumn>
					</FormRow>
				{/if}

				{#if validated && !conditionValid}
					<div bind:this={missingConditionError} class="error-message">
						Please select a diagnosis, or <b>None of the above</b>
					</div>
				{/if}
			</div>

			<FormRow>
				<FormColumn>
					<YesNoGroup
						{disabled}
						required
						name="facebookSupportGroupMember"
						label="Are you a member of any support groups or Facebook groups?"
						bind:value={$model.personalMedicalHistory.facebookSupportGroupMember}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.personalMedicalHistory && $model.personalMedicalHistory.facebookSupportGroupMember === "Yes"}
				<FormRow>
					<FormColumn>
						<TextArea
							label="Please specify the names of the groups and any additional information you are willing to provide about them"
							required
							{disabled}
							bind:value={$model.personalMedicalHistory.facebookSupportGroupInfo}
							id="facebookSupportGroupInfo"
						/>
					</FormColumn>
				</FormRow>
			{/if}
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<SaveAndClose>Save and Close</SaveAndClose>
			</FormColumn>
		</FormRow>

		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<NavigationButtons bind:buttons nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"} />
			</FormColumn>
		</FormRow>
	</Form>
</section>

<style>
</style>
